class ProductCardSlider {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.product-card-slider__track');
    this.originalSlides = Array.from(container.querySelectorAll('.product-card-slider__slide'));
    this.prevBtn = container.querySelector('.product-card-swiper__prev');
    this.nextBtn = container.querySelector('.product-card-swiper__next');
    this.totalSlides = this.originalSlides.length;
    this.slideWidth = 0;
    this.containerWidth = 0;
    this.currentPosition = 0;
    this.animationId = null;
    this.animationStart = 0;
    this.animationFrom = 0;
    this.animationTo = 0;
    this.duration = 500;

    // Drag state
    this.isDragging = false;
    this.dragDidMove = false;
    this.dragPointerId = null;
    this.dragStartX = 0;
    this.dragStartPos = 0;
    this.dragDelta = 0;
    this.dragStartTime = 0;
    this.threshold = 40;

    // Hover-preview state: at the first slide we peek at slide 2,
    // at the last slide we peek at slide N-1. Direction records which way.
    this.previewActive = false;
    this.previewDirection = 0;

    // On the first user-driven slide action, force all images in *this*
    // slider to load eagerly so subsequent slides don't blink in.
    this.imagesEagered = false;

    if (this.totalSlides > 1) {
      this.setupLoop();
      this.bindEvents();
      this.observeForWarm();
    }
  }

  // The peeked slide (slide 2) is translated off-screen, so native lazy-loading
  // never fetches it — the old code only started the fetch on hover, i.e. the
  // instant it became visible. Warm it as the card nears the viewport instead,
  // so it's decoded before the user hovers. rootMargin gives a head start.
  observeForWarm() {
    if (typeof IntersectionObserver === 'undefined') { this.eagerLoadFirstImages(3); return; }
    if (!ProductCardSlider.warmObserver) {
      ProductCardSlider.warmObserver = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (entry.target.__slider) entry.target.__slider.eagerLoadFirstImages(3);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '300px' });
    }
    this.container.__slider = this;
    ProductCardSlider.warmObserver.observe(this.container);
  }

  setupLoop() {
    var firstClone = this.originalSlides[0].cloneNode(true);
    var lastClone = this.originalSlides[this.totalSlides - 1].cloneNode(true);

    firstClone.setAttribute('data-clone', 'true');
    lastClone.setAttribute('data-clone', 'true');

    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, this.track.firstChild);

    this.currentIndex = 1;
    this.measureSlideWidth();
    this.setPosition(-(this.currentIndex * this.slideWidth));
  }

  measureSlideWidth() {
    // Use actual layout positions so margin/gap and subpixel rounding are accounted for.
    var slides = this.track.querySelectorAll('.product-card-slider__slide');
    if (slides.length >= 2) {
      var a = slides[0].getBoundingClientRect();
      var b = slides[1].getBoundingClientRect();
      this.slideWidth = b.left - a.left;
    } else {
      this.slideWidth = this.container.getBoundingClientRect().width;
    }
    this.containerWidth = this.container.offsetWidth;
  }

  updateSlideWidth() {
    this.measureSlideWidth();
  }

  bindEvents() {
    this.nextBtn && this.nextBtn.addEventListener('click', this.next.bind(this));
    this.prevBtn && this.prevBtn.addEventListener('click', this.prev.bind(this));
    this.boundOnResize = this.onResize.bind(this);
    window.addEventListener('resize', this.boundOnResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.boundOnResize);
      this.resizeObserver.observe(this.container);
    }

    // Drag events
    this.track.addEventListener('pointerdown', this.onDragStart.bind(this));
    this.track.addEventListener('pointermove', this.onDragMove.bind(this));
    this.track.addEventListener('pointerup', this.onDragEnd.bind(this));
    this.track.addEventListener('pointercancel', this.onDragEnd.bind(this));
    this.track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    this.track.addEventListener('click', this.onClickAfterDrag.bind(this), true);

    // Hover preview (desktop only)
      this.boundOnMouseEnter = this.onMouseEnter.bind(this);
      this.boundOnMouseLeave = this.onMouseLeave.bind(this);
      this.container.addEventListener('mouseenter', this.boundOnMouseEnter);
      this.container.addEventListener('mouseleave', this.boundOnMouseLeave);
  }

  // --- Drag handlers ---

  onDragStart(e) {
    if (e.button && e.button !== 0) return;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Pointer-down is the earliest signal that the user intends to swipe —
    // start loading images now so they're decoded by the time the slide lands.
    this.eagerLoadImages();

    // If the user starts dragging while hovering on an edge slide, commit
    // the previewed slide so drag math continues from the visible position.
    this.commitPreview();

    this.isDragging = true;
    this.dragDidMove = false;
    this.dragPointerId = e.pointerId;
    this.dragStartX = e.clientX;
    this.dragStartPos = this.currentPosition;
    this.dragDelta = 0;
    this.dragStartTime = Date.now();
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    this.dragDelta = e.clientX - this.dragStartX;

    if (!this.dragDidMove && Math.abs(this.dragDelta) > this.threshold) {
      this.dragDidMove = true;
      this.track.setPointerCapture(this.dragPointerId);
      this.track.style.cursor = 'grabbing';
    }

    if (this.dragDidMove) {
      var newPos = this.dragStartPos + this.dragDelta;
      this.setPosition(newPos);
    }
  }

  onDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.track.style.cursor = '';

    if (!this.dragDidMove) return;

    var elapsed = Date.now() - this.dragStartTime;
    var velocity = this.dragDelta / elapsed;
    var absDelta = Math.abs(this.dragDelta);
    var isSwipe = absDelta > this.threshold || Math.abs(velocity) > 0.3;

    if (isSwipe && this.dragDelta < 0) {
      this.handleLoopJump();
      this.currentIndex++;
    } else if (isSwipe && this.dragDelta > 0) {
      this.handleLoopJump();
      this.currentIndex--;
    }

    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  onClickAfterDrag(e) {
    if (this.dragDidMove) {
      e.preventDefault();
      e.stopPropagation();
      this.dragDidMove = false;
    }
  }

  // --- Resize ---

  onResize() {
    var newWidth = this.container.offsetWidth;
    if (newWidth === this.containerWidth) return;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    // Resize invalidates the preview's visual offset; reset it so the next
    // mouseenter can trigger cleanly.
    this.previewActive = false;
    this.previewDirection = 0;
    this.measureSlideWidth();
    this.setPosition(-(this.currentIndex * this.slideWidth));
  }

  // --- Hover preview ---

  onMouseEnter() {
    // On hover, preload the first few images so the hover-preview and
    // a couple of arrow clicks have warm images — without yanking every
    // image on the page out of lazy mode.
    this.eagerLoadFirstImages(4);

    if (this.isDragging || this.previewActive) return;
    if (this.totalSlides < 2) return;

    var target = null;
    var direction = 0;
    if (this.currentIndex === 1) {
      // At first slide → peek forward at slide 2
      target = this.currentIndex + 1;
      direction = 1;
    } else if (this.currentIndex === this.totalSlides) {
      // At last slide → peek backward at slide N-1
      target = this.currentIndex - 1;
      direction = -1;
    }
    if (target === null) return;

    // Instant swap, no animation
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.previewActive = true;
    this.previewDirection = direction;
    this.setPosition(-(target * this.slideWidth));
  }

  onMouseLeave() {
    if (!this.previewActive) return;

    // Instant swap back, no animation
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.previewActive = false;
    this.previewDirection = 0;
    this.setPosition(-(this.currentIndex * this.slideWidth));
  }

  commitPreview() {
    if (!this.previewActive) return;
    // The previewed slide is the new "current" slide; subsequent next/prev
    // advances from there, naturally producing the skip-the-previewed-slide
    // behavior (e.g., from slide 1 hover→2, next → 3).
    this.currentIndex += this.previewDirection;
    this.previewActive = false;
    this.previewDirection = 0;
  }

  eagerLoadImages() {
    if (this.imagesEagered) return;
    this.imagesEagered = true;
    var imgs = this.container.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      this.markImageEager(imgs[i]);
    }
  }

  eagerLoadFirstImages(count) {
    if (this.imagesEagered) return;
    var imgs = this.container.querySelectorAll('img');
    var limit = Math.min(count, imgs.length);
    for (var i = 0; i < limit; i++) {
      this.markImageEager(imgs[i]);
    }
    if (limit >= imgs.length) this.imagesEagered = true;
  }

  markImageEager(img) {
    if (img.loading === 'lazy') img.loading = 'eager';
    if ('fetchPriority' in img && !img.fetchPriority) img.fetchPriority = 'high';
  }

  // --- Arrow navigation ---

  next() {
    this.eagerLoadImages();
    this.commitPreview();
    this.handleLoopJump();
    this.currentIndex++;
    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  prev() {
    this.eagerLoadImages();
    this.commitPreview();
    this.handleLoopJump();
    this.currentIndex--;
    this.animateTo(-(this.currentIndex * this.slideWidth));
  }

  handleLoopJump() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
      this.setPosition(this.animationTo);
    }

    if (this.currentIndex === 0) {
      this.currentIndex = this.totalSlides;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    } else if (this.currentIndex > this.totalSlides) {
      this.currentIndex = 1;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    }
  }

  // --- Animation ---

  easeCubicBezier(t) {
    var cx = 3 * 0.35;
    var bx = 3 * (0.15 - 0.35) - cx;
    var ax = 1 - cx - bx;

    var cy = 3 * 0;
    var by = 3 * (1 - 0) - cy;
    var ay = 1 - cy - by;

    var s = t;
    for (var i = 0; i < 8; i++) {
      var xS = ((ax * s + bx) * s + cx) * s;
      var dxS = (3 * ax * s + 2 * bx) * s + cx;
      if (Math.abs(dxS) < 1e-6) break;
      s = s - (xS - t) / dxS;
    }

    s = Math.max(0, Math.min(1, s));
    return ((ay * s + by) * s + cy) * s;
  }

  animateTo(target) {
    var self = this;

    this.animationFrom = this.currentPosition;
    this.animationTo = target;
    this.animationStart = performance.now();

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    function tick(now) {
      var elapsed = now - self.animationStart;
      var progress = Math.min(elapsed / self.duration, 1);
      var eased = self.easeCubicBezier(progress);
      var pos = self.animationFrom + (self.animationTo - self.animationFrom) * eased;

      self.setPosition(pos);

      if (progress < 1) {
        self.animationId = requestAnimationFrame(tick);
      } else {
        self.animationId = null;
        self.onAnimationComplete();
      }
    }

    this.animationId = requestAnimationFrame(tick);
  }

  setPosition(pos) {
    this.currentPosition = pos;
    this.track.style.transform = 'translate3d(' + pos + 'px, 0px, 0px)';
  }

  onAnimationComplete() {
    if (this.currentIndex === 0) {
      this.currentIndex = this.totalSlides;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    } else if (this.currentIndex === this.totalSlides + 1) {
      this.currentIndex = 1;
      this.setPosition(-(this.currentIndex * this.slideWidth));
    }
  }

  // --- Destroy (for reinit) ---

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize);
    }
    if (this.boundOnMouseEnter) {
      this.container.removeEventListener('mouseenter', this.boundOnMouseEnter);
      this.container.removeEventListener('mouseleave', this.boundOnMouseLeave);
    }
    if (ProductCardSlider.warmObserver) {
      ProductCardSlider.warmObserver.unobserve(this.container);
    }
    delete this.container.__slider;
    this.track.style.transform = '';
    var clones = this.track.querySelectorAll('[data-clone]');
    clones.forEach(function (clone) { clone.remove(); });
    this.originalSlides.forEach(function (slide) { slide.style.width = ''; });
    delete this.container.dataset.sliderInit;
  }
}

window.initProductCardSlider = function (selector) {
  selector = selector || '.product-card-slider';

  document.querySelectorAll(selector).forEach(function (el) {
    if (el.dataset.sliderInit) return;
    el.dataset.sliderInit = 'true';

    new ProductCardSlider(el);
  });
};

document.addEventListener('DOMContentLoaded', function () {
  window.initProductCardSlider();
});

document.addEventListener('shopify:section:load', function () {
  window.initProductCardSlider();
});
